// apps/client/src/hooks/useSendOutboundMessage.ts
//
// Wraps the `sendOutboundMessage` callable (Worktree B,
// services/client-functions/src/conversations/sendOutboundMessage.ts).
//
// Canonical plan §1.8 contract:
//   Input:  { companyId, conversationId, text (≤ 2000 chars) }
//   Output: { mid (the real Meta message_id), metaMessageId }
//   Throws HttpsError:
//     - 'failed-precondition' / window_expired
//     - 'failed-precondition' / flag_not_enforce
//     - 'permission-denied'   / not assigned + not admin
//     - 'aborted'             / send_succeeded_but_local_state_diverged
//                              (sent_unconfirmed — reconciler sweeps it)
//     - 'internal'            / Send API rejected
//
// Optimistic UI strategy:
//   - Caller appends a pending message to local state with mid=`local:<uuid>`.
//   - On success, the Firestore subscription delivers the real doc (the
//     callable did the swap server-side); caller drops the optimistic
//     doc by mid.
//   - On HttpsError, caller drops the optimistic doc and surfaces the
//     error message inline.
// This hook does NOT manage the optimistic doc itself; that lives in
// ConversationComposer.tsx so the composer can correlate by its own
// generated uuid without coupling state across components.
import { useCallback, useState } from 'react';
import { httpsCallable, FunctionsError } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface SendOutboundMessageArgs {
  companyId: string;
  conversationId: string;
  text: string;
}

export interface SendOutboundMessageResult {
  /** The real Meta message_id once swap-committed. */
  mid: string;
  metaMessageId: string;
}

interface UseSendOutboundMessageState {
  send: (args: SendOutboundMessageArgs) => Promise<SendOutboundMessageResult>;
  sending: boolean;
  error: string | null;
  /**
   * Discrete error code for UI branching. Maps directly onto the
   * HttpsError `code`/`details.code` documented above. `null` until a
   * failed send occurs, then sticks until the next send attempt.
   */
  errorCode: string | null;
}

export function useSendOutboundMessage(): UseSendOutboundMessageState {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const send = useCallback(
    async (
      args: SendOutboundMessageArgs
    ): Promise<SendOutboundMessageResult> => {
      setSending(true);
      setError(null);
      setErrorCode(null);
      try {
        const fn = httpsCallable<
          SendOutboundMessageArgs,
          SendOutboundMessageResult
        >(functions, 'sendOutboundMessage');
        const result = await fn(args);
        return result.data;
      } catch (err) {
        const fnErr = err as FunctionsError;
        // FunctionsError exposes `.code` ('functions/<code>') and
        // `.details` (server-side payload). Surface both so the composer
        // can render a typed banner.
        const code =
          (fnErr?.details as { code?: string } | undefined)?.code ??
          fnErr?.code ??
          'unknown';
        const message =
          fnErr instanceof Error ? fnErr.message : String(err);
        setError(message);
        setErrorCode(code);
        // Log read failures explicitly per
        // [[feedback-silent-fail-catch]] — composer surfaces the banner,
        // but the console trace helps trace back HttpsError details.
        console.error('[useSendOutboundMessage] send failed', code, message);
        throw err;
      } finally {
        setSending(false);
      }
    },
    []
  );

  return { send, sending, error, errorCode };
}
